import type { OrderDetail, PaymentMethod, PaymentStatus } from "@billiard/contracts";
import { stringifyCsv } from "./csv.js";

export interface OrderReportRecord {
  id: OrderDetail["id"];
  businessDate: OrderDetail["businessDate"];
  tableNumber: OrderDetail["tableNumber"];
  status: OrderDetail["status"];
  offlineOrigin: OrderDetail["offlineOrigin"];
  syncState: OrderDetail["syncState"];
  tableFeeFen: OrderDetail["tableFeeFen"];
  productTotalFen: OrderDetail["productTotalFen"];
  coachServiceTotalFen: OrderDetail["coachServiceTotalFen"];
  totalFen: OrderDetail["totalFen"];
  paidFen: OrderDetail["paidFen"];
  refundedFen: OrderDetail["refundedFen"];
  payments: ReadonlyArray<Pick<OrderDetail["payments"][number], "method" | "status">>;
  startedAt: OrderDetail["startedAt"];
  endedAt: OrderDetail["endedAt"];
}

const ORDER_REPORT_HEADERS = [
  "订单编号",
  "营业日",
  "球台",
  "订单状态",
  "开单来源",
  "同步状态",
  "台费（分）",
  "商品金额（分）",
  "助教服务金额（分）",
  "订单总额（分）",
  "已收金额（分）",
  "已退款金额（分）",
  "收款方式",
  "付款状态",
  "开台时间（ISO 8601）",
  "结束时间（ISO 8601）",
] as const;

export function orderReportOrderStatusLabel(status: OrderDetail["status"]): string {
  switch (status) {
    case "OPEN":
      return "进行中";
    case "SETTLED":
      return "已结账";
    case "REFUNDED":
      return "已退款";
    case "CONFLICT":
      return "待核对";
  }
}

export function orderReportSyncStateLabel(state: OrderDetail["syncState"]): string {
  switch (state) {
    case "PENDING":
      return "待同步";
    case "SYNCED":
      return "已同步";
    case "CONFLICT":
      return "同步冲突";
    case "FAILED":
      return "同步失败";
    case "ISOLATED":
      return "已隔离待核对";
  }
}

export function orderReportPaymentMethodLabel(method: PaymentMethod): string {
  switch (method) {
    case "CASH":
      return "现金";
    case "STATIC_WECHAT":
      return "微信静态收款码";
    case "STATIC_ALIPAY":
      return "支付宝静态收款码";
    case "MEMBER_BALANCE":
      return "会员余额";
    case "WECHAT_PAY":
      return "微信支付";
    case "ALIPAY":
      return "支付宝";
  }
}

export function orderReportPaymentStatusLabel(status: PaymentStatus): string {
  switch (status) {
    case "CONFIRMED_LOCAL":
      return "本店已确认";
    case "PENDING_RECONCILIATION":
      return "待核对到账";
    case "PENDING_MEMBER_DEBIT":
      return "待联网扣会员余额";
    case "MEMBER_RECEIVABLE":
      return "会员应收";
    case "MANUALLY_RECONCILED":
      return "已人工核对";
    case "RECONCILIATION_EXCEPTION":
      return "核对异常";
    case "PARTIALLY_REFUNDED":
      return "部分退款";
    case "REFUNDED":
      return "已退款";
    case "CONFIRMED_CLOUD":
      return "云端已确认";
  }
}

export function orderReportOfflineOriginLabel(offlineOrigin: boolean): string {
  return offlineOrigin ? "离线开单" : "在线开单";
}

export function buildOrderReportCsv(orders: ReadonlyArray<OrderReportRecord>): string {
  return stringifyCsv([
    ORDER_REPORT_HEADERS,
    ...orders.map((order) => [
      order.id,
      order.businessDate,
      order.tableNumber,
      orderReportOrderStatusLabel(order.status),
      orderReportOfflineOriginLabel(order.offlineOrigin),
      orderReportSyncStateLabel(order.syncState),
      order.tableFeeFen,
      order.productTotalFen,
      order.coachServiceTotalFen,
      order.totalFen,
      order.paidFen,
      order.refundedFen,
      order.payments.length > 0
        ? order.payments.map((payment) => orderReportPaymentMethodLabel(payment.method)).join("、")
        : "未收款",
      order.payments.length > 0
        ? order.payments.map((payment) => orderReportPaymentStatusLabel(payment.status)).join("、")
        : "未收款",
      order.startedAt,
      order.endedAt ?? "",
    ]),
  ]);
}

export function orderReportFilename(businessDate?: string): string {
  return `订单清单-${reportDateSuffix(businessDate, "全部营业日")}.csv`;
}

export function orderReportContentDisposition(businessDate?: string): string {
  const asciiFilename = `orders-${reportDateSuffix(businessDate, "all")}.csv`;
  return `attachment; filename="${asciiFilename}"; filename*=UTF-8''${encodeURIComponent(
    orderReportFilename(businessDate),
  )}`;
}

function reportDateSuffix(businessDate: string | undefined, fallback: string): string {
  if (businessDate === undefined) return fallback;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(businessDate)) {
    throw new RangeError("businessDate must use YYYY-MM-DD");
  }
  return businessDate;
}
